import React from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import { bindActionCreators } from "redux";
import moment from "moment";
import _ from "lodash";
import { Button } from "react-bootstrap";
import { CSVLink } from "react-csv";
import { Popconfirm } from "antd";

import { closeCustomModal, openCustomModal } from "components/custom/modals";
import { fetchStores, clearStores } from "api/menu/master-files/stores/actions";
import { fetchHelperJobsLookup, fetchJobsLookup } from "api/menu/master-files/job-maintenance/actions";
import { methodRemoveAllWorkers } from "actions/draftActions";
import UserTypes from "components/auth/user-types";

const FillMissingWorkerModal = React.lazy(() =>
    import("components/schedule/DraftSchedules/fill-missing-worker-modal"));

class Buttons extends React.Component {
    csvLink = React.createRef();

    constructor(props) {
        super(props);
        this.state = {
            total: [],

            exportCSVData: []
        };

        this.statusButtons = {
            InProgress: {
                title: "In Progress",
                key: "InProgress",
                allow: ["ReadyForSupervisor", "Completed"],
                requirementsComplete: true
            },
            ReadyForSupervisor: {
                title: "Ready For Supervisor",
                key: "ReadyForSupervisor",
                allow: ["InProgress", "Completed"],
                requirementsComplete: true
            },
            Completed: {
                title: "Completed",
                key: "Completed",
                allow: ["InProgress", "Exported"],
                requirementsComplete: true
            },
            Exported: {
                title: "Exported",
                key: "Exported",
                allow: [],
                requirementsComplete: true
            }
        };

        this.handleExportData = this.handleExportData.bind(this);
        this.fillMissingWorkers = this.fillMissingWorkers.bind(this);
        this.removeAllWorkers = this.removeAllWorkers.bind(this);
        this.makeRequests = this.makeRequests.bind(this);
    }

    componentDidMount() {
        this.makeRequests(this.props);
    }

    componentWillUpdate(nextProps) {
        this.statusButtons.Completed.requirementsComplete = true;
        if (!nextProps.draftsDetails.length) {
            this.statusButtons.Completed.requirementsComplete = false;
        } else {
            const teamLeadAvailabilityStatusExclude = ["Inactive", "FullyBlocked"];
            // eslint-disable-next-line no-restricted-syntax,guard-for-in
            for (const index in nextProps.draftsDetails) {
                if (nextProps.draftsDetails[index].hours <= 0) {
                    this.statusButtons.Completed.requirementsComplete = false;
                    break;
                }

                if (!nextProps.draftsDetails[index].workers.length) {
                    this.statusButtons.Completed.requirementsComplete = false;
                    break;
                } else {
                    const teamLeadCount = nextProps.draftsDetails[index].workers.filter(
                        value =>
                            value.isTeamLead &&
                            teamLeadAvailabilityStatusExclude.indexOf(
                                value.availabilityStatus
                            ) === -1
                    ).length;
                    if (teamLeadCount === 0) {
                        this.statusButtons.Completed.requirementsComplete = false;
                        break;
                    }
                }
            }
        }
    }

    componentDidUpdate(prevProps) {
        if (prevProps.drafts !== this.props.drafts) {
            this.makeRequests(this.props);
        }
    }

    componentWillUnmount() {
        this.props.clearStores();
        this.props.fetchHelperJobsLookup(undefined, true);
    }

    makeRequests = (props) => {
        const { draftIds, drafts } = props;
        if (/* draftIds.length === 1 && */ drafts.length) {
            let retailerIdIdArr = [];
            draftIds.forEach(draftId => {
                const draft = drafts.find(item => item.id === parseInt(draftId, 10));
                retailerIdIdArr.push(draft.retailerId);
            });

            retailerIdIdArr = _.uniq(retailerIdIdArr);
            // const draft = drafts.find(item => item.id === parseInt(draftIds[0], 10));
            // console.error("draft", retailerIdIdArr, draft);
            props.fetchStores(retailerIdIdArr);
            props.fetchHelperJobsLookup(retailerIdIdArr);
        }
    };

    buttonIsDisabled = (buttonValue, currentStatus) => {
        if (typeof currentStatus === "undefined") {
            return true;
        } else if (buttonValue === currentStatus) {
            return true;
        } else if (!this.statusButtons[buttonValue].requirementsComplete) {
            return true;
        } else if (!this.statusButtons[currentStatus]) {
            return true;
        } else if (!Array.isArray(this.statusButtons[currentStatus].allow)) {
            return true;
        } else if (
            this.statusButtons[currentStatus].allow.indexOf(buttonValue) === -1
        ) {
            return true;
        }
        return false;
    };

    handleExportData = () => {
        this.props.handleExportData(exportCSVData => {
            this.setState(
                {
                    exportCSVData
                },
                () => {
                    // click the CSVLink component to trigger the CSV download
                    this.csvLink.current.link.click();
                }
            );
        });
    };

    fillMissingWorkers = () => {
        const modalObj = {
            id: "fill-missing-workers-modal",
            modalSettings: {
                size: "sm"
            },
            header: "Fill Missing Workers",
            children: (
                <React.Suspense fallback={<div className="m-2 text-center font-weight-bold">Loading...</div>}>
                    <FillMissingWorkerModal
                        retailerId={this.props.retailerId}
                        metadata={this.props.metadata}
                        onCancel={() => this.props.closeCustomModal(modalObj)}
                    />
                </React.Suspense>
            ),
            handleClose: () => this.props.closeCustomModal(modalObj)
        };
        this.props.openCustomModal(modalObj);
    };

    removeAllWorkers = () => {
        this.props.methodRemoveAllWorkers(this.props.metadata);
    };

    render() {
        const {
            selectedDraftsIds,
            handleSetDraftStatus,
            drafts,
            auth,
            retailerId
        } = this.props;

        const isSupervisor = auth.user.role.includes(UserTypes.supervisor);

        const currentDraftId = parseInt(selectedDraftsIds[0], 10);
        let currentDraft = false;
        // eslint-disable-next-line no-restricted-syntax
        for (const index in drafts) {
            if (drafts[index].id === currentDraftId) {
                currentDraft = drafts[index];
                break;
            }
        }

        const showRemoveAllWorkersButton =
            auth.user.role.includes(UserTypes.accountManager) ||
            auth.user.role.includes(UserTypes.admin);

        return (
            <div className="d-flex justify-content-between  mt-4 mb-4">
                <div className="w-50 d-flex justify-content-start">
                    <Button
                        variant="info"
                        onClick={this.handleExportData}
                    >
                        Download as CSV
                    </Button>

                    {!retailerId ||
                        <Button
                            variant="info"
                            onClick={this.fillMissingWorkers}
                            className="ml-2"
                        >
                            Fill missing workers
                        </Button>
                    }

                    {retailerId && showRemoveAllWorkersButton ?
                        <Popconfirm
                            title="Remove all workers?"
                            onConfirm={this.removeAllWorkers}
                            placement="top"
                            okText="Yes"
                            cancelText="Cancel"
                        >
                            <Button
                                variant="danger"
                                className="ml-2"
                            >
                                Remove all workers
                            </Button>
                        </Popconfirm>
                    : null}

                    <CSVLink
                        data={this.state.exportCSVData}
                        filename={`export_${moment().format("YYYY-MM-DD")}.csv`}
                        target="_blank"
                        className="text-white"
                        ref={this.csvLink}
                    />
                </div>

                <div className="w-50 d-flex ">
                    <span className="d-inline-flex align-items-center text-center allocation-legend-inactive text-light">Inactive</span>
                    <span className="d-inline-flex align-items-center text-center allocation-legend-full-block">Full block</span>
                    <span className="d-inline-flex align-items-center text-center allocation-legend-part-block">Partial block</span>
                    <span className="d-inline-flex align-items-center text-center allocation-legend-over">Overallocated</span>
                </div>

                {isSupervisor ||
                    <div className="w-50 d-flex justify-content-end">
                        {Object.keys(this.statusButtons).map(key => {
                            const button = this.statusButtons[key];
                            return (
                                <Button
                                    key={key}
                                    variant={`${
                                        currentDraft.status === button.key ? "success" : "info"
                                        }`}
                                    value={button.title}
                                    onClick={() => handleSetDraftStatus(currentDraftId, button.key)}
                                    disabled={this.buttonIsDisabled(
                                        button.key,
                                        currentDraft.status
                                    )}
                                    className="ml-2"
                                >
                                    {button.title}
                                </Button>
                            );
                        })}
                    </div>
                }
            </div>
        );
    }
}

Buttons.propTypes = {
    stores: PropTypes.array.isRequired,
    drafts: PropTypes.array,
    draftsDetails: PropTypes.array.isRequired,
    handleExportData: PropTypes.func.isRequired,
    fetchStores: PropTypes.func.isRequired,
    clearStores: PropTypes.func.isRequired,
    handleSetDraftStatus: PropTypes.func.isRequired,
    fetchJobsLookup: PropTypes.func.isRequired,
    retailerId: PropTypes.oneOfType([PropTypes.number, PropTypes.bool]).isRequired
};

const mapStateToProps = state => ({
    stores: state.stores.stores,
    helperJobs: state.jobs.helperJobsLookup,
    jobs: state.jobs.jobsLookup,
    auth: state.auth,
});

const mapStateToDispatch = dispatch => ({
    methodRemoveAllWorkers: bindActionCreators(methodRemoveAllWorkers, dispatch),
    fetchStores: bindActionCreators(fetchStores, dispatch),
    clearStores: bindActionCreators(clearStores, dispatch),
    fetchHelperJobsLookup: bindActionCreators(fetchHelperJobsLookup, dispatch),
    fetchJobsLookup: bindActionCreators(fetchJobsLookup, dispatch),

    openCustomModal: bindActionCreators(openCustomModal, dispatch),
    closeCustomModal: bindActionCreators(closeCustomModal, dispatch)
});

export default connect(
    mapStateToProps,
    mapStateToDispatch
)(Buttons);
